import React, { useEffect } from "react";
import { sortBy, isEmpty } from "lodash";
import {
  H5,
  H6,
  Divider,
  Box,
  RadioButton,
  Checkbox,
  Badge,
  Flex,
} from "orcs-design-system";
import { useHistory } from "react-router-dom";
import { getSearchPath } from "src/consts/urlPaths";
import MatchPropType from "src/custom-prop-types/match";

import { useGlobalSearchContext } from "src/components/Search/GlobalSearchContext";
import useUpdateEffect from "src/hooks/useUpdateEffect";
import { getGroupTypeBadgeVariant } from "src/util/group";
import {
  useWorkspace,
  useWorkspaceFeatureFlags,
} from "src/contexts/global/WorkspaceContext";
import { trackEvent } from "src/services/segment";
import { trackExploreJourneyEvent } from "src/services/exploreOrganisationAnalytics";
import { EVENT_TRACKING } from "src/consts/eventTracking";
import {
  filterTypeLabels,
  filterTypes,
  positionFieldFilterItems,
  positionOccupancyStatus,
  positionOccupancyStatusApiValues,
  positionOccupancyStatusFilterOptions,
} from "../../../pages/SearchPage/SearchPage.config";
import FilterSearchSection from "./FilterSearchSection";

const FilterSearchResults = ({ match }) => {
  const {
    searchTerm,
    peopleFilters,
    setPeopleFilters,
    defaultTeamTypes,
    setDefaultTeamTypes,
    addPeopleFilter,
    removePeopleFilter,
    addDefaultTeamType,
    removeDefaultTeamType,
    positionFilters,
    setPositionFilters,
    addPositionFilter,
    removePositionFilter,
    positionStatus,
    setPositionStatus,
    debouncedSearchTerm,
  } = useGlobalSearchContext();
  const { enablePositionBackedVacancies } = useWorkspaceFeatureFlags();

  const { params } = match;
  const { type, searchTerm: urlSearchTerm } = params;
  const history = useHistory();
  // Use URL when in sync or on initial load (searchTerm undefined); debouncedSearchTerm when user has typed something different.
  const effectiveSearchTerm =
    searchTerm === urlSearchTerm || searchTerm == null
      ? urlSearchTerm
      : debouncedSearchTerm;

  const trackSearchFilter = ({
    emitLegacy = true,
    legacyProps,
    journeyProps = {},
  }) => {
    // LEGACY until 2026-11-17 — keep prior check-only volume for legacy dashboards.
    if (emitLegacy && legacyProps) {
      trackEvent(EVENT_TRACKING.SEARCH_FILTERED, legacyProps);
    }
    trackExploreJourneyEvent(EVENT_TRACKING.SEARCH_FILTER_COMPLETED, {
      lifecycle_stage: "completed",
      entry_point: "filter_sidebar",
      operation_intent: "search",
      ...journeyProps,
    });
  };

  const onHandleNavigate = (searchType, query) => (e) => {
    setPeopleFilters([]);
    setDefaultTeamTypes([]);
    setPositionFilters([]);
    setPositionStatus(positionOccupancyStatus.ALL);
    history.push(getSearchPath(searchType, query));
    e.stopPropagation();
    e.preventDefault();
    trackSearchFilter({
      legacyProps: { setting: "filter", value: searchType },
      journeyProps: {
        filter_setting: "filter",
        filter_value: searchType,
        filter_checked: true,
      },
    });
  };

  const onHandleSubfilter = (e) => {
    const checked = e.target.checked;
    if (checked) {
      addPeopleFilter(e.target.name);
      trackSearchFilter({
        legacyProps: { setting: "sub_filter", value: e.target.name },
        journeyProps: {
          filter_setting: "sub_filter",
          filter_value: e.target.name,
          filter_checked: true,
        },
      });
    } else {
      removePeopleFilter(e.target.name);
      trackSearchFilter({
        emitLegacy: false,
        journeyProps: {
          filter_setting: "sub_filter",
          filter_value: e.target.name,
          filter_checked: false,
        },
      });
    }
  };

  const onHandlePositionFieldFilter = (e) => {
    const checked = e.target.checked;
    if (checked) {
      addPositionFilter(e.target.name);
      trackSearchFilter({
        legacyProps: {
          setting: "position_field_filter",
          value: e.target.name,
        },
        journeyProps: {
          filter_setting: "position_field_filter",
          filter_value: e.target.name,
          filter_checked: true,
        },
      });
    } else {
      removePositionFilter(e.target.name);
      trackSearchFilter({
        emitLegacy: false,
        journeyProps: {
          filter_setting: "position_field_filter",
          filter_value: e.target.name,
          filter_checked: false,
        },
      });
    }
  };

  const onHandlePositionStatus = (status) => (e) => {
    setPositionStatus(status);
    trackSearchFilter({
      legacyProps: { setting: "position_status", value: status },
      journeyProps: {
        filter_setting: "position_status",
        filter_value: status,
        filter_checked: true,
      },
    });
    e.stopPropagation();
    e.preventDefault();
  };

  const onHandleTeamType = (e) => {
    const checked = e.target.checked;
    if (checked) {
      addDefaultTeamType(e.target.name);
      trackSearchFilter({
        legacyProps: { setting: "team_type", value: true },
        journeyProps: {
          filter_setting: "team_type",
          filter_value: e.target.name,
          filter_checked: true,
        },
      });
    } else {
      removeDefaultTeamType(e.target.name);
      trackSearchFilter({
        emitLegacy: false,
        journeyProps: {
          filter_setting: "team_type",
          filter_value: e.target.name,
          filter_checked: false,
        },
      });
    }
  };

  useUpdateEffect(() => {
    const url = getSearchPath(type, effectiveSearchTerm, false);
    const searchParams = [];
    if (!isEmpty(peopleFilters)) {
      searchParams.push(`filters=${peopleFilters.join()}`);
    }

    if (!isEmpty(defaultTeamTypes)) {
      searchParams.push(`team_types=${defaultTeamTypes.join()}`);
    }

    if (!isEmpty(positionFilters)) {
      searchParams.push(`position_filters=${positionFilters.join()}`);
    }

    if (positionStatus && positionStatus !== positionOccupancyStatus.ALL) {
      searchParams.push(`position_status=${positionStatus}`);
    }

    const currentUrl = !isEmpty(searchParams)
      ? `${url}?${searchParams.join("&")}`
      : url;
    const prevPath = history?.location?.search
      ? `${history?.location?.pathname}${history?.location?.search}`
      : history?.location?.pathname;

    if (decodeURI(currentUrl) !== decodeURI(prevPath)) {
      history.push(currentUrl);
    }
  }, [
    peopleFilters,
    defaultTeamTypes,
    positionFilters,
    positionStatus,
    type,
    effectiveSearchTerm,
    history,
  ]);

  // Parse URL query parameters on mount and when type changes
  useEffect(() => {
    const searchParams = new URLSearchParams(history.location.search);
    const filtersParam = searchParams.get("filters");
    const teamTypesParam = searchParams.get("team_types");
    const positionFiltersParam = searchParams.get("position_filters");
    const positionStatusParam = searchParams.get("position_status");

    if (filtersParam) {
      const filters = filtersParam.split(",").filter(Boolean);
      setPeopleFilters(filters);
    } else {
      setPeopleFilters([]);
    }

    if (teamTypesParam) {
      const teamTypes = teamTypesParam.split(",").filter(Boolean);
      setDefaultTeamTypes(teamTypes);
    } else {
      setDefaultTeamTypes([]);
    }

    if (positionFiltersParam) {
      setPositionFilters(positionFiltersParam.split(",").filter(Boolean));
    } else {
      setPositionFilters([]);
    }

    if (positionOccupancyStatusApiValues.includes(positionStatusParam)) {
      setPositionStatus(positionStatusParam);
    } else {
      setPositionStatus(positionOccupancyStatus.ALL);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    type,
    setPeopleFilters,
    setDefaultTeamTypes,
    setPositionFilters,
    setPositionStatus,
  ]);

  const workspace = useWorkspace();

  return (
    <div id="keep-open-on-click-outside">
      <H5 fontWeight="bold">Search filters</H5>
      <Box my="r">
        <Divider light />
      </Box>
      <Flex flexDirection="column" gap="s">
        <H6 fontWeight="bold" mb="s">
          What are you looking for?
        </H6>
        <RadioButton
          name="search-filter-btn"
          label={filterTypeLabels.ALL}
          checked={type === filterTypes.ALL}
          onClick={(e) => {
            onHandleNavigate(filterTypes.ALL, effectiveSearchTerm)(e);
          }}
        />
        <RadioButton
          name="search-filter-btn"
          label={filterTypeLabels.PEOPLE}
          checked={type === filterTypes.PEOPLE}
          onClick={(e) => {
            onHandleNavigate(filterTypes.PEOPLE, effectiveSearchTerm)(e);
          }}
        />
        {!!enablePositionBackedVacancies && (
          <RadioButton
            name="search-filter-btn"
            label={filterTypeLabels.POSITIONS}
            checked={type === filterTypes.POSITIONS}
            onClick={(e) => {
              onHandleNavigate(filterTypes.POSITIONS, effectiveSearchTerm)(e);
            }}
          />
        )}
        <RadioButton
          name="search-filter-btn"
          label={filterTypeLabels.TEAM}
          checked={type === filterTypes.TEAMS}
          onClick={(e) => {
            onHandleNavigate(filterTypes.TEAMS, effectiveSearchTerm)(e);
          }}
        />
        <RadioButton
          name="search-filter-btn"
          label={filterTypeLabels.TAGS}
          checked={type === filterTypes.TAGS}
          onClick={(e) => {
            onHandleNavigate(filterTypes.TAGS, effectiveSearchTerm)(e);
          }}
        />

        {type === filterTypes.PEOPLE && (
          <FilterSearchSection
            items={[
              { label: "First name", name: "firstName" },
              { label: "Last name", name: "surname" },
              { label: "Job Title", name: "jobTitle" },
            ]}
            onChange={onHandleSubfilter}
            checkedItems={peopleFilters}
          />
        )}
        {type === filterTypes.POSITIONS && !!enablePositionBackedVacancies && (
          <>
            <H6 fontWeight="bold" mt="s">
              Filter by status:
            </H6>
            {positionOccupancyStatusFilterOptions.map(({ value, label }) => (
              <RadioButton
                key={value}
                name="position-status-filter"
                label={label}
                checked={positionStatus === value}
                onClick={onHandlePositionStatus(value)}
              />
            ))}
            <FilterSearchSection
              items={positionFieldFilterItems}
              onChange={onHandlePositionFieldFilter}
              checkedItems={positionFilters}
            />
          </>
        )}
        {type === filterTypes.TEAMS && (
          <>
            <H6 fontWeight="bold" my="s">
              Filter results by team type:
            </H6>
            {sortBy(
              Object.values(workspace?.config?.groupTypes || {}),
              "name"
            ).map((groupType) => {
              return (
                <Checkbox
                  key={groupType.id}
                  label={
                    <Badge variant={getGroupTypeBadgeVariant(groupType)}>
                      {groupType.name}
                    </Badge>
                  }
                  name={groupType.id}
                  checked={defaultTeamTypes.includes(groupType.id)}
                  onChange={onHandleTeamType}
                />
              );
            })}
          </>
        )}
      </Flex>
    </div>
  );
};

FilterSearchResults.propTypes = {
  match: MatchPropType.isRequired,
};

export default FilterSearchResults;
